import { useRef, useState } from 'react'

import { ActStrip, useConfirmAfterApplied } from './ActStrip.jsx'
import FinishPanel from './FinishPanel.jsx'
import { MemberActionRow, ProgressStepper } from './WatchPanel.jsx'
import {
  catchUpTarget,
  caughtUpAtLastAired,
  caughtUpNextInfo,
  episodeCode,
  hasReviewText,
  joinableId,
  nextEpisodeToOffer,
  subscriptionsOf,
  weekdayEasternDate,
} from './watch.js'
import { READY, fetchArchive } from './watchArchive.js'
import { nextFavoriteRank } from './watchMember.js'
import {
  KIND_SET_FAVORITE_RANK,
  TARGET_STOPPED,
  TARGET_WATCHED,
  favoriteRankCommand,
  useWatchRequest,
} from './watchRequests.js'

// V2-8a: the shared action bar — `ActionBar.dc.html`'s own reference
// markup, HANDOFF.md's "Bot action states" section and the brief's own
// shared-pieces item 5: "for a series: +1 (gated), 'I've seen up to
// SxEy · finale', the watching/stopped status control." For a movie, the
// single "I watched it."
//
// 🔴 V2-4c ADDS the two controls this file's own header used to say it
// never draws: the heart (`FavoriteHeart` below — `watch.set_favorite_rank`
// now exists, V2-4b) and Rate it / Review it (`RateReviewControl` —
// `watch.set_rating`/`watch.set_review` now exist, V2-4b). Both are drawn
// exactly where `ActionBar.dc.html`'s own `bar()` builds them: alongside
// the status control, never replacing it — the design's `finishOpen` panel
// is a SIBLING of the status buttons, not a swap, so a member can still see
// (and change) their watching/stopped status while the FinishPanel is open.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THIS COMPONENT OWNS NO REQUEST LOGIC OF ITS OWN — it composes the TWO
//    ALREADY-SHARED controls (`MemberActionRow`, `ProgressStepper`,
//    `WatchPanel.jsx`) rather than re-deriving their gating, their
//    confirm-after-applied predicate, or their payloads a second time.
//    "Do not write a second request hook" (the brief, verbatim) — both
//    controls keep their OWN, INDEPENDENT `useWatchRequest()` instance,
//    unchanged from before this component existed: a member can still
//    press "+1" and "I've stopped watching" as two genuinely separate
//    in-flight requests, exactly as the card already allows. Unifying the
//    two into one shared pending state would be a real behaviour change
//    this slice does not make — see docs/reports/V2-8a.md.
// ═══════════════════════════════════════════════════════════════════════════
//
// The status control (`MemberActionRow`) is ALWAYS relevant regardless of
// whether there is a next episode to advance to, so it renders
// unconditionally.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 MANAGER REVIEW ROUND 2 — `progressFallback` NO LONGER COVERS THE
//    "CAUGHT UP" CASE SPECIFICALLY. EVERY OTHER "NOTHING TO OFFER" REASON
//    STILL GETS IT, EXACTLY AS BEFORE.
// ═══════════════════════════════════════════════════════════════════════════
//
// V2-3b §10.1's own ruling still stands for the genuinely UNREACHABLE case:
// `ProgressStepper`'s `unavailable` prop swaps in the read-only copy-the-
// command control the moment a press reveals the platform service cannot
// be reached — that mechanism is untouched.
//
// What changed is narrower than "nothing to offer" as a whole: the first
// draft of this component sent EVERY "no target" case to `progressFallback`
// — a season boundary, no position of your own, nothing aired at all, AND
// being genuinely caught up all landed on the same read-only control. That
// broke the first three (`ProgressControl` is still the only honest thing
// to show for them — see its own docstring), and it was never what
// HANDOFF.md draws for the fourth: the prototype's `bar()` has a THIRD
// shape for exactly "caught up, with a dated next episode already known" —
// `blocked`, with its own label ("+1 once S4E8 airs · Tue 22 Sep"), never
// the copy-command box. Since P6-283, the episode list already lets a
// member mark ANY aired episode directly, which is what makes the old
// fallback's job redundant for the CAUGHT-UP case specifically — a command
// that would just tell you to mark yourself at your own current position
// has nothing left to do.
//
// So now: a real target to offer mounts the stepper; caught up with a
// dated next episode shows the blocked label; caught up with NO dated next
// episode shows nothing (never a guessed date, per `caughtUpNextInfo`'s
// own contract, `watch.js`); every OTHER "nothing to offer" reason (a
// season boundary, catching up past more than one episode, no position of
// your own, nothing aired at all) still falls back to `progressFallback`,
// unchanged from before this review round.
//
// `ember`, always `true` on `ProgressStepper` here: HANDOFF's own colour
// role, "primary buttons (+1, 'I've seen up to…')" — this bar is always
// the page's own primary action surface, never a secondary one the way a
// list row's copy of the same button might read.

/**
 * V2-4c: the heart — brief §2, "adds a favourite at the next free rank, or
 * removes one (`rank: null`), through `watch.set_favorite_rank`, on any
 * catalog title. Dusk is the heart's colour" (RECONCILIATION item 3).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * 🔴 "NEXT FREE RANK" NEEDS THE WHOLE ARCHIVE, WHICH THIS COMPONENT DOES
 *    NOT PERSISTENTLY POLL
 * ═══════════════════════════════════════════════════════════════════════
 *
 * A favourite can be ANY catalog title, listed or not (Jordan's ruling,
 * `docs/reports/V2-4b.md` §4) — so "the next free rank" can only be
 * answered from the ARCHIVE (`watchMember.js::memberFavorites`, "ranked
 * across the WHOLE archive"), never from the one `subscription` this
 * component already holds. `ActionBar` mounts on every row of a
 * watchlist AND the title page, so calling the archive's own polling hook
 * (`useWatchArchive`) here would mean one independent fetch loop PER ROW —
 * this instead calls `fetchArchive()` once, only at the moment the heart
 * is actually pressed to turn ON (never on mount, never on an interval),
 * the same "two independent fetchers, one discipline" choice
 * `watchArchive.js`'s own header already makes for `fetchArchive`/
 * `fetchWatch`. Turning a favourite OFF needs no lookup at all — `rank:
 * null` is unconditionally correct, so that path skips this entirely.
 *
 * The one-off fetch can itself fail (offline, a slow network) — surfaced
 * as `rankLookupFailed`, a small inline note, NEVER as a guessed rank
 * (adding at rank 1 on a failed lookup could silently bump an existing
 * favourite out of first place).
 */
function FavoriteHeart({ subscription, userId, onChanged }) {
  const request = useWatchRequest()
  const [rankLookupFailed, setRankLookupFailed] = useState(false)
  const [computing, setComputing] = useState(false)
  const askedRankRef = useRef(null)
  const mine = subscription?.members?.find((member) => joinableId(member.id) === userId) ?? null
  const currentRank =
    typeof mine?.favorite_rank === 'number' && Number.isFinite(mine.favorite_rank) ? mine.favorite_rank : null
  const isFav = currentRank !== null

  const isConfirmed = (shared) => {
    const fresh = subscriptionsOf(shared).find((row) => row.id === subscription?.id)
    const freshMine = fresh?.members?.find((member) => joinableId(member.id) === userId)
    return (freshMine?.favorite_rank ?? null) === askedRankRef.current
  }
  const confirmFailed = useConfirmAfterApplied(request, onChanged, isConfirmed)

  const send = (rank) => {
    askedRankRef.current = rank
    request.ask({ kind: KIND_SET_FAVORITE_RANK, target: subscription.id, payload: { rank } })
  }

  const press = async () => {
    if (isFav) {
      send(null)
      return
    }
    setRankLookupFailed(false)
    setComputing(true)
    const answer = await fetchArchive()
    setComputing(false)
    if (answer.state !== READY) {
      setRankLookupFailed(true)
      return
    }
    send(nextFavoriteRank(answer.shared, userId))
  }

  if (request.status) {
    return (
      <ActStrip
        request={request}
        confirmFailed={confirmFailed}
        testId="action-favorite-status"
        confirmStaleTestId="action-favorite-confirm-stale"
        doneText={
          askedRankRef.current === null
            ? 'Removed from your favorites.'
            : `Added to your favorites · #${askedRankRef.current}.`
        }
        command={favoriteRankCommand(subscription?.title, askedRankRef.current)}
        onRetry={() => send(askedRankRef.current)}
      />
    )
  }

  return (
    <>
      <button
        type="button"
        aria-pressed={isFav}
        className={`d2d-actionbar__heart${isFav ? ' d2d-actionbar__heart--on' : ''}`}
        data-testid="action-favorite-heart"
        disabled={computing}
        onClick={press}
      >
        {isFav ? `★ Favorite #${currentRank}` : '☆ Favorite'}
      </button>
      {rankLookupFailed ? (
        <span className="d2d-micro d2d-actionbar__favfail" data-testid="action-favorite-check-failed">
          Couldn’t check your favorites — try again.
        </span>
      ) : null}
    </>
  )
}

/**
 * V2-4c: Rate it / Review it — `ActionBar.dc.html`'s own `a.canRate`/
 * `a.rateLabel`/`a.onRate`/`a.finishOpen`/`a.fp`, verbatim shape (the
 * prototype's `bar()` builds all of this alongside the status control,
 * never as a separate section). Shown once the member has finished or
 * stopped this title — never before, since there is nothing yet to rate.
 *
 * The label mirrors the design's own three-way rule exactly: already
 * rated or reviewed reads "Rated N/10 · change" / "Reviewed · change";
 * otherwise "Review it" for a member who stopped, "Rate it" for one who
 * finished. `hasReviewText` (`watch.js`) is the SAME predicate
 * `WatchTitle.jsx`'s own Reviews section already uses to decide whether a
 * review has real text, reused rather than re-typed (gotcha 1).
 */
function RateReviewControl({ subscription, userId, onChanged }) {
  const [open, setOpen] = useState(false)
  const mine = subscription?.members?.find((member) => joinableId(member.id) === userId) ?? null
  const finishedOrStopped = mine?.status === TARGET_WATCHED || mine?.status === TARGET_STOPPED
  if (!finishedOrStopped) return null

  if (open) {
    return (
      <FinishPanel
        subscription={subscription}
        userId={userId}
        onChanged={onChanged}
        onClose={() => setOpen(false)}
      />
    )
  }

  const hasRating = typeof mine?.rating === 'number' && Number.isFinite(mine.rating)
  const hasFeedback = hasRating || hasReviewText(mine)
  const label = hasFeedback
    ? `${hasRating ? `Rated ${mine.rating}/10` : 'Reviewed'} · change`
    : mine?.status === TARGET_STOPPED
      ? 'Review it'
      : 'Rate it'

  return (
    <button
      type="button"
      className="d2d-actionbar__ratebtn"
      data-testid="action-rate-review"
      onClick={() => setOpen(true)}
    >
      {label}
    </button>
  )
}

/**
 * `subscription`, `yourProgress`, `userId`, `you` (`deriveYou`'s own
 * shape) and `onChanged` are exactly what `MemberActionRow`/
 * `ProgressStepper` already take. `progressFallback`, when given, covers
 * two cases: the platform service turning out to be unreachable
 * (`ProgressStepper`'s own `unavailable` prop, mid-press), and every
 * "nothing to offer" reason that is NOT "you're caught up" (a season
 * boundary, no position of your own, nothing aired at all). Being
 * genuinely caught up never reaches it — that renders this component's own
 * `blocked` label, or nothing at all, per `caughtUpAtLastAired`/
 * `caughtUpNextInfo` (`watch.js`). A zero-argument function returning JSX
 * (a render prop), never a rendered element (which would mount even when
 * unused).
 */
export function ActionBar({ subscription, yourProgress, userId, you, onChanged, progressFallback }) {
  if (!you.signedIn) return null

  const isMovie = subscription?.kind === 'movie'
  // The `isMovie` exclusion lives ONLY in the JSX ternary below — folding
  // it into this variable too, as an earlier draft did, made the two
  // checks redundant, and a mutation of EITHER one alone was unkillable
  // because the OTHER still excluded the movie. One gate, asserted by the
  // defensive fixture in watch-buttons.test.jsx ("ActionBar never mounts a
  // progress stepper for a movie, even given an (unrealistic) last_aired").
  const hasProgressTarget =
    Boolean(nextEpisodeToOffer(subscription, yourProgress) || catchUpTarget(subscription, yourProgress))
  // "Caught up" is the ONLY "nothing to offer" reason that retires the
  // copy-command fallback (manager review round 2) — a season boundary, no
  // position of your own, or nothing aired at all keep it exactly as
  // before, via `progressFallback` below.
  const caughtUp = !isMovie && !hasProgressTarget && caughtUpAtLastAired(subscription, yourProgress)
  const blocked = caughtUp ? caughtUpNextInfo(subscription, yourProgress) : null

  return (
    <div className="d2d-actionbar" data-testid="action-bar">
      <MemberActionRow subscription={subscription} you={you} userId={userId} onChanged={onChanged} />
      {isMovie ? null : hasProgressTarget ? (
        <ProgressStepper
          subscription={subscription}
          yourProgress={yourProgress}
          userId={userId}
          onChanged={onChanged}
          ember
          unavailable={progressFallback}
        />
      ) : caughtUp ? (
        blocked ? (
          <span className="d2d-actionbar__blocked" data-testid="action-blocked">
            {`+1 once ${episodeCode(blocked.season, blocked.episode)} airs · ${weekdayEasternDate(blocked.airsAtMs)}`}
          </span>
        ) : null
      ) : (
        progressFallback?.() ?? null
      )}
      <FavoriteHeart subscription={subscription} userId={userId} onChanged={onChanged} />
      <RateReviewControl subscription={subscription} userId={userId} onChanged={onChanged} />
    </div>
  )
}

export default ActionBar
